// Derives the human-readable "pricing basis" line the trade-invoice dialogs
// render above the amount inputs, and the reference price that pre-fills
// `market_reference_price` from the parent ticket.

export interface TicketPricingInputs {
  readonly pricing_type: string | null;
  readonly signed_price: string | null;
  readonly lme_price: string | null;
  readonly premium_discount: string | null;
  readonly payable_percent: string | null;
}

function numOrNull(s: string | null): number | null {
  if (s === null) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function pricingBasisLine(t: TicketPricingInputs): string {
  switch (t.pricing_type) {
    case 'Fixed':
      return 'Fixed';
    case 'Formula': {
      const pct = numOrNull(t.payable_percent);
      return pct === null ? 'Formula' : `Formula ${String(pct)}%`;
    }
    case 'Index': {
      const pd = numOrNull(t.premium_discount);
      if (pd === null) return 'LME';
      const sign = pd >= 0 ? '+' : '−';
      return `LME ${sign}${String(Math.abs(pd))}`;
    }
    default:
      return '—';
  }
}

// Reference price pre-filled into `market_reference_price`:
//   Fixed / Formula → ticket.signed_price
//   Index           → ticket.lme_price + ticket.premium_discount
// Returns null when the inputs needed for the pricing type are missing.
export function referencePriceFromTicket(t: TicketPricingInputs): number | null {
  if (t.pricing_type === 'Fixed' || t.pricing_type === 'Formula') {
    return numOrNull(t.signed_price);
  }
  if (t.pricing_type === 'Index') {
    const lme = numOrNull(t.lme_price);
    if (lme === null) return null;
    const pd = numOrNull(t.premium_discount) ?? 0;
    return lme + pd;
  }
  return null;
}
